import type { Goal, Task } from '../types/timeline'
import { getFirebase } from './firebase'
import { syncStockTaskLinks } from './stockService'
import { stripUndefinedDeep } from './syncSanitize'

const getTimelineDoc = async (uid: string) => {
  const firebase = await getFirebase()
  return firebase.firestore().collection('users').doc(uid).collection('timeline').doc('state')
}

export const loadUserGoals = async (uid: string): Promise<Goal[]> => {
  const timelineDoc = await getTimelineDoc(uid)
  const snapshot = await timelineDoc.get()
  if (!snapshot.exists) return []

  const data = snapshot.data() as { goals?: unknown }
  return Array.isArray(data.goals) ? (data.goals as Goal[]) : []
}

const collectTasks = (goals: Goal[]): Task[] => goals.flatMap((goal) => goal.tasks ?? [])

const diffStockLinks = (before: Task[], after: Task[]) => {
  const beforeMap = new Map(before.map((task) => [task.id, task.requiredStockIds ?? []]))
  const changes: Array<{ taskId: string; before: string[]; after: string[] }> = []
  for (const task of after) {
    const prev = beforeMap.get(task.id) ?? []
    const next = task.requiredStockIds ?? []
    if (prev.length !== next.length || prev.some((id, index) => id !== next[index])) {
      changes.push({ taskId: task.id, before: prev, after: next })
    }
    beforeMap.delete(task.id)
  }
  // Remaining entries were deleted tasks — drop their links too.
  for (const [taskId, prev] of beforeMap.entries()) {
    if (prev.length > 0) changes.push({ taskId, before: prev, after: [] })
  }
  return changes
}

export const saveUserGoals = async (
  uid: string,
  goals: Goal[],
  options: { workspaceId?: string | null; previousGoals?: Goal[] } = {},
) => {
  const sanitizedGoals = stripUndefinedDeep(goals)
  const timelineDoc = await getTimelineDoc(uid)
  await timelineDoc.set(
    {
      goals: sanitizedGoals,
      updatedAt: new Date().toISOString(),
    },
    { merge: true },
  )

  // Soft-coupled link sync — only runs if caller supplies a workspace context.
  // Treats Task.requiredStockIds as source of truth and updates the back-ref
  // on the corresponding StockItem.linkedTaskIds. Best-effort, never throws.
  if (options.workspaceId) {
    const changes = diffStockLinks(collectTasks(options.previousGoals ?? []), collectTasks(goals))
    await Promise.allSettled(
      changes.map((change) => syncStockTaskLinks(options.workspaceId!, change.taskId, change.before, change.after)),
    )
  }
}
